<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { globalSearch, type SearchHit } from '@/features/search/search.api';
import { SEARCH_KINDS, groupHits } from '@/features/search/search-kinds';
import AppSpinner from '@/shared/ui/AppSpinner.vue';

/**
 * Search across every kind of record, reachable from anywhere.
 *
 * Opened with Ctrl/Cmd+K or by the button in the rail. A dialog rather than a field in the
 * header: the results need room, and a panel that pushes the page around while somebody is
 * reading it is worse than one that covers it.
 */
/**
 * `onRail` only chooses colours: the rail is graphite and the mobile header is white, and
 * one component serving both beats two that drift apart.
 */
const { onRail = false } = defineProps<{ onRail?: boolean }>();

const router = useRouter();

const open = ref(false);
const term = ref('');
const hits = ref<SearchHit[]>([]);
const tooShort = ref(false);
const searching = ref(false);
const highlighted = ref(0);
const input = ref<HTMLInputElement | null>(null);

const groups = computed(() => groupHits(hits.value));

/** The panel appears once there is something to say, not the moment the field is focused. */
const showResults = computed(() => open.value && term.value.trim() !== '');

/**
 * Positioned against the viewport and teleported out, for the same reason the notification
 * panel is: the rail is a scroll container with `overflow-hidden`, and a scroll container
 * clips on BOTH axes. A 28rem panel anchored inside a 15rem rail loses everything past the
 * rail's edge.
 */
const position = ref({ left: 0, top: 0, width: 0 });

function place(): void {
  const field = input.value?.getBoundingClientRect();

  if (!field) {
    return;
  }

  const width = Math.min(448, window.innerWidth - 16);

  position.value = {
    left: Math.max(8, Math.min(field.left, window.innerWidth - width - 8)),
    top: field.bottom + 4,
    width,
  };
}

watch(showResults, (visible) => {
  if (visible) {
    place();
  }
});

watch(term, () => {
  if (showResults.value) {
    place();
  }
});

/**
 * The panel follows the field rather than being dismissed by a scroll.
 *
 * Closing on scroll was wrong twice over. Focusing the field scrolls it into view, which
 * fires a scroll event, which dismissed the panel before it ever appeared — the feature
 * simply did not work. And even without that, a panel that vanishes because the page moved
 * an inch under it is a panel that punishes an accidental trackpad brush.
 */
function reposition(): void {
  if (showResults.value) {
    place();
  }
}

/** Flattened in display order, so the arrow keys walk what the eye walks. */
const flat = computed(() => groups.value.flatMap((group) => group.hits));

/**
 * "See everything" is the last stop, not the first.
 *
 * Enter still opens the highlighted result, which is the fast path and the reason anybody
 * uses a palette. This row is where you land by continuing past the last hit, and it is
 * the only thing there when nothing matched — so "I did not find it, Enter" reaches the
 * full results either way.
 */
const seeAllIndex = computed(() => flat.value.length);

let debounce: ReturnType<typeof setTimeout> | undefined;
let inFlight: AbortController | undefined;

watch(term, (value) => {
  clearTimeout(debounce);
  highlighted.value = 0;

  if (value.trim() === '') {
    hits.value = [];
    tooShort.value = false;
    searching.value = false;
    return;
  }

  searching.value = true;

  debounce = setTimeout(() => {
    // The previous request is abandoned rather than awaited: typing fast otherwise means
    // an older answer can land after a newer one and overwrite it.
    inFlight?.abort();
    inFlight = new AbortController();

    void globalSearch(value, undefined, inFlight.signal)
      .then((result) => {
        hits.value = result.hits;
        tooShort.value = result.tooShort;
      })
      .catch(() => {
        // An aborted request is the normal case here, not a failure worth reporting.
        hits.value = [];
      })
      .finally(() => {
        searching.value = false;
      });
  }, 200);
});

async function show(): Promise<void> {
  open.value = true;
  await nextTick();
  input.value?.focus();
  input.value?.select();
}

function close(): void {
  open.value = false;
  term.value = '';
  hits.value = [];
  inFlight?.abort();
}

function go(hit: SearchHit): void {
  close();
  void router.push(SEARCH_KINDS[hit.kind].route(hit));
}

function seeAll(): void {
  const q = term.value.trim();
  close();
  void router.push({ name: 'search', query: { q } });
}

function move(delta: number): void {
  // One past the hits is the "see everything" row, so the count is length + 1.
  const stops = flat.value.length + 1;

  highlighted.value = (highlighted.value + delta + stops) % stops;
}

function onKeydown(event: KeyboardEvent): void {
  // Ctrl+K on Windows and Linux, Cmd+K on a Mac: the shortcut people already try.
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault();
    void (open.value ? close() : show());
    return;
  }

  if (!open.value) {
    return;
  }

  if (event.key === 'Escape') {
    close();
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    move(1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    move(-1);
  } else if (event.key === 'Enter') {
    event.preventDefault();

    const hit = flat.value[highlighted.value];

    if (hit) {
      go(hit);
    } else {
      seeAll();
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', reposition);
  window.addEventListener('scroll', reposition, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', reposition);
  window.removeEventListener('scroll', reposition, true);
  clearTimeout(debounce);
  inFlight?.abort();
});

defineExpose({ show });
</script>

<template>
  <div class="relative">
    <!--
      The field IS the search, not a button that opens one.
      
      It used to be a fake input that raised a modal containing the real input: a control
      that looks like something you can type into and is not, followed by a second one that
      is. Typing here searches here.
    -->
    <div
      class="flex items-center gap-2 rounded-lg border px-3 py-2 transition"
      :class="onRail
        ? 'border-white/10 bg-white/5 focus-within:border-white/25'
        : 'border-border bg-surface focus-within:border-brand-500'"
    >
      <span
        class="text-[12.5px]"
        :class="onRail ? 'text-rail-ink-muted' : 'text-ink-muted'"
        aria-hidden="true"
      >⌕</span>
      <input
        ref="input"
        v-model="term"
        type="search"
        autocomplete="off"
        spellcheck="false"
        placeholder="Buscar…"
        aria-label="Buscar en todo el sistema"
        :class="onRail
          ? 'w-full min-w-0 bg-transparent text-[12.5px] text-rail-ink placeholder:text-rail-ink-muted focus:outline-none'
          : 'w-full min-w-0 bg-transparent text-sm text-ink placeholder:text-ink-muted focus:outline-none'"
        @focus="open = true"
      >
      <AppSpinner
        v-if="searching"
        class="size-3.5 shrink-0"
      />
      <!-- Shown rather than only documented, or nobody discovers the shortcut. -->
      <kbd
        v-else-if="term === ''"
        class="rounded border px-1 text-[10px] tracking-wide"
        :class="onRail ? 'border-white/15 text-rail-ink-muted' : 'border-border text-ink-muted'"
      >⌘K</kbd>
    </div>

    <Teleport to="body">
      <button
        v-if="showResults"
        type="button"
        class="fixed inset-0 z-40 cursor-default"
        aria-label="Cerrar los resultados"
        @click="close"
      />
    </Teleport>

    <!--
      Anchored to the field rather than centred on the screen: the results belong to the
      thing being typed into, and a panel in the middle of the page severs that link.
    -->
    <Teleport to="body">
      <div
        v-if="showResults"
        class="fixed z-50 overflow-hidden rounded-card border border-border bg-surface shadow-lg"
        :style="{
          left: `${position.left}px`,
          top: `${position.top}px`,
          width: `${position.width}px`,
        }"
      >
        <div class="max-h-[60vh] overflow-y-auto">
          <p
            v-if="term.trim() === ''"
            class="px-4 py-8 text-center text-sm text-ink-muted"
          >
            Escriba para buscar en todo el sistema.
          </p>

          <!--
              Said plainly. An empty list here would read as "nothing matches" and send
              somebody looking for a spelling mistake that is not there.
            -->
          <p
            v-else-if="tooShort"
            class="px-4 py-8 text-center text-sm text-ink-muted"
          >
            Escriba al menos dos letras.
          </p>

          <p
            v-else-if="!searching && hits.length === 0"
            class="px-4 py-8 text-center text-sm text-ink-muted"
          >
            No se encontró nada para «{{ term }}».
          </p>

          <template
            v-for="group in groups"
            :key="group.kind"
          >
            <p class="px-4 pt-3 pb-1 text-[10.5px] font-medium tracking-[0.1em] text-ink-muted uppercase">
              {{ group.label }}
            </p>
            <ul>
              <li
                v-for="hit in group.hits"
                :key="`${hit.kind}-${hit.id}`"
              >
                <button
                  type="button"
                  class="block w-full px-4 py-2.5 text-left"
                  :class="flat[highlighted] === hit ? 'bg-brand-50' : 'hover:bg-surface-muted'"
                  @click="go(hit)"
                  @mouseenter="highlighted = flat.indexOf(hit)"
                  @focus="highlighted = flat.indexOf(hit)"
                >
                  <span class="block truncate text-sm font-medium text-ink">{{ hit.title }}</span>
                  <span class="block truncate text-xs text-ink-muted">{{ hit.subtitle }}</span>
                </button>
              </li>
            </ul>
          </template>

          <!--
            Always present, even with nothing above it: "I did not find it, Enter" and
            "there was nothing, Enter" both have to end somewhere useful.
          -->
          <button
            v-if="!tooShort"
            type="button"
            class="block w-full border-t border-border px-4 py-2.5 text-left text-sm"
            :class="highlighted === seeAllIndex ? 'bg-brand-50' : 'hover:bg-surface-muted'"
            @click="seeAll"
            @mouseenter="highlighted = seeAllIndex"
            @focus="highlighted = seeAllIndex"
          >
            <span class="font-medium text-brand-700">
              Ver todos los resultados de «{{ term.trim() }}»
            </span>
          </button>
        </div>

        <p class="border-t border-border px-4 py-2 text-[11px] text-ink-muted">
          ↑↓ para moverse · Enter para abrir · Esc para cerrar
        </p>
      </div>
    </Teleport>
  </div>
</template>
